import {tns as tinySlider} from "tiny-slider/src/tiny-slider";
import {MD_WIDTH, LG_WIDTH} from "@main/helpers/const.js";

/**
 * Класс для управления слайдерами
 */
export class Slider {
    /**
     * @param params
     * @param callbacks
     * @param methods
     */
    constructor({
        params = {
            container: ".tiny-slider",
            preventScrollOnTouch: "auto",
        },
        callbacks = {},
        methods = {}
    }) {
        if (params) {
            Object.keys(params).forEach(key => {
                if (typeof params[key] === "function") {
                    const fn = params[key];

                    params[key] = () => fn.call(this, this);
                }
            });
        }

        this.options = params;
        this.selector = this.options.container;
        this.callbacks = callbacks;
        this.methods = methods;
        this.tinySlider = undefined;

        this.mount();
    }

    /**
     * Инициализация слайдера с опциями
     */
    mount() {
        this.container = document.querySelector(this.selector);

        if (this.container) {
            this.tinySlider = tinySlider(this.options);

            this.bindSliderFunctions();
        }
    }

    /**
     * Возвращает информацию по текущему слайдеру.
     * Функция для сокращения существующей записи.
     *
     * @returns {TinySliderInfo}
     */
    getInfoAboutSlider() {
        return this.tinySlider.getInfo();
    }

    /**
     * Подключение функций для слайдера
     */
    bindSliderFunctions() {
        let callbacks = this.callbacks;
        let methods = this.methods;

        if (callbacks) {
            Object.keys(callbacks).forEach(key => {
                if (typeof callbacks[key] === "function") {
                    this.tinySlider.events.on(key, () => callbacks[key].call(this, this));
                }
            })
        }

        if (methods) {
            Object.keys(methods).forEach(key => {
                if (typeof methods[key] === "function") {
                    methods[key].call(this, this);
                }
            })
        }
    }

    /**
     * Заполнение прогресс бара в контейнере с точками при инциализации
     *
     * @param container
     * @param filler
     * @param dots
     */
    fillProgressBarOnInit({container, filler, dots}) {
        const containerNode = document.querySelector(container);
        const fillerNode = containerNode.querySelector(filler);
        const dotsNodes = containerNode.querySelectorAll(dots);        

        dotsNodes.forEach(dot => {             
            
            if (dot.classList.contains("tns-nav-active")) {
                const dotWidth = dot.offsetWidth;
                const offsetLeftDots = containerNode.offsetLeft;
                let fillerNodeWidth;
                let dotPosX;

                if (window.innerWidth < MD_WIDTH) {
                    dotPosX = dot.offsetLeft;
                    fillerNodeWidth = dotPosX + (dotWidth / 2);
                    fillerNode.style.width = fillerNodeWidth + "px";
                } else {
                    dotPosX = dot.getBoundingClientRect().left;
                    fillerNodeWidth = dotPosX - offsetLeftDots + (dotWidth / 2);
                    fillerNode.style.width = fillerNodeWidth + "px";
                }
            }
        });
    }

    /**
     * Заполнение прогресс бара в контейнере с тосками при скролле
     *
     * @param container
     * @param filler
     * @param dots
     */
    fillProgressBarOnScroll({container, filler, dots}) {
        const containerNode = document.querySelector(container);
        const fillerNode = containerNode.querySelector(filler);
        const dotsNodes = containerNode.querySelectorAll(dots);        

        dotsNodes.forEach(dot => {             
            
            if (dot.classList.contains("tns-nav-active")) {
                const dotWidth = dot.offsetWidth;
                const offsetLeftDots = containerNode.offsetLeft;
                const dotPosX = dot.getBoundingClientRect().left;
                let fillerNodeWidth;

                if (window.innerWidth < MD_WIDTH) {
                    fillerNodeWidth = dotPosX + (dotWidth / 2);
                    fillerNode.style.width = fillerNodeWidth + "px";
                } else {
                    fillerNodeWidth = dotPosX - offsetLeftDots + (dotWidth / 2);
                    fillerNode.style.width = fillerNodeWidth + "px";
                } 
            }
        });
    }

    /**
     * Изменение стиля у предыдущих точек
     *
     * @param container
     * @param dots
     */
    changeStylePrevDots({container, dots}) {
        const containerNode = document.querySelector(container);
        const dotsNodes = containerNode.querySelectorAll(dots);        

        dotsNodes.forEach(dot => { 
            if (dot.classList.contains("tns-nav-active")) {
                let prevSiblings = this.getAllPrevSiblingsDots(dot);
                let nextSiblings = this.getAllNextSiblingsDots(dot);
                prevSiblings.forEach(prevElem => {
                    prevElem.classList.add("history-dots__item--prev");                    
                });
                nextSiblings.forEach(nextElem => {
                    if (nextElem.classList.contains("history-dots__item--prev")) {
                        nextElem.classList.remove("history-dots__item--prev")
                    }
                });
            }
        });
    }   

    /**
     * Отступы между точками пейджера в виде годов.
     * Рассчитывает процентное соотношение каждого элемента от максимального года и вычитает уже добавленные отступы.
     *
     * @param container
     * @param dots
     */
    dotsMarginByYear({container, dots}) {
        const containerNode = document.querySelector(container);
        const dotsNodes = containerNode.querySelectorAll(dots);
        const marginContainer = 3;

        if (window.innerWidth >= LG_WIDTH) {
            dotsNodes.forEach((dot) => {
                const dotWidth = dot.clientWidth;
                const dotsMargin = containerNode.clientWidth / dotsNodes.length - dotWidth - marginContainer;
                let calculatedMargin = dotsMargin + (dotsMargin / dotsNodes.length);
                
                dot.style = `margin-right: ${calculatedMargin}px`;
                dotsNodes[dotsNodes.length - 1].style = "margin-right: 0";
            })
        }
    }

    /**
     * Переключение активного слайда при клике на слайд с указанным селектором.
     *
     * @param selector
     * @param slideIndex
     */
    handleGoToSlideClick(selector = ".history-slide") {
        const thumbs = this.container.querySelectorAll(selector);

        if (window.innerWidth < LG_WIDTH) {
            thumbs.forEach((thumb) => {
                thumb.addEventListener("touchmove", () => {
                    const container = document.querySelector(".js-dots-nav-container");
                    const activeDot = container.querySelector(".tns-nav-active");
                    activeDot.scrollIntoView({block: "nearest", inline: "center", behavior: "smooth"});
                });
            })  
        }
    }

    getAllPrevSiblingsDots(dot) {
        let prevSiblings = [];
        let sibling = dot;
        while (sibling.previousElementSibling) {
            sibling = sibling.previousElementSibling;
            prevSiblings.push(sibling);
        }
        return prevSiblings;
    }

    getAllNextSiblingsDots(dot) {
        let nextSiblings = [];
        let sibling = dot;
        while (sibling.nextElementSibling) {
            sibling = sibling.nextElementSibling;
            nextSiblings.push(sibling);
        }
        return nextSiblings;
    }
}
